import { RouteProps } from 'react-router-dom';
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    useAuth0,
    withAuthenticationRequired,
    WithAuthenticationRequiredOptions,
} from '@auth0/auth0-react';
import AuthSpinner from '../AuthSpinner/AuthSpinner';

import { useHybridAuth } from './HybdridAuthProvider';

interface IHybridAuthProps extends RouteProps {
    component: React.ReactElement;
    withAuthRequiredOptions?: WithAuthenticationRequiredOptions | undefined;
}

export const HybridAuth: React.FC<IHybridAuthProps> = ({ component, withAuthRequiredOptions }) => {
    const { isAuthenticated: isAuth0Authenticated, isLoading: isAuth0Loading } = useAuth0();
    const { isAuthenticated: isTokenAuthenticated, loading: isTokenLoading } = useHybridAuth();

    // Mémorisation du composant enveloppé pour éviter les recréations à chaque rendu
    const WrappedComponent = useMemo(
        () =>
            withAuthenticationRequired(() => component, {
                onRedirecting: () => <AuthSpinner />,
                ...withAuthRequiredOptions,
            }),
        [component, withAuthRequiredOptions],
    );

    // État de chargement global
    if (isAuth0Loading || isTokenLoading) {
        return <AuthSpinner />;
    }

    // Authentification Auth0 réussie
    if (isAuth0Authenticated) {
        return <WrappedComponent />;
    }

    // Authentification par token réussie
    if (isTokenAuthenticated) {
        return component as any;
    }

    // Aucune authentification - déclenche la redirection Auth0
    return <WrappedComponent />;
};

export const MagicLinkAuthHandler = () => {
    const navigate = useNavigate();
    const { isAuthenticated, error, loading } = useHybridAuth();

    useEffect(() => {
        if (loading) return;
        if (isAuthenticated) {
            navigate(`/dashboard`, { replace: true });
        } else if (error) {
            navigate('/login', { replace: true });
        }
    }, [isAuthenticated, error, loading, navigate]);

    return <AuthSpinner />;
};
